import type { Product } from '../../types/product';
import type { Prefecture } from '../../utils/prefectures';

export interface ProductTemplateFieldType {
  listingType: 'auction' | 'fixed';
    itemNumber: string;
    itemNumber2: string;
    startPrice?: number;
    buyItNowPrice?: number;
    itemNumberPrefix: boolean;
    itemNumberSuffix: boolean;
    itemNumberSerial: boolean;
    memoPrefix: boolean;
    memoSuffix: boolean;
    memoSerial: boolean;
    condition: string;
    description: string;
    descriptionTemplateId: string | null;
    categoryYahoo: string; // Yahoo category field
    shippingLater: boolean;
    shippingResponsibility: 'buyer' | 'seller' | '';
    shippingTime: '1-2' | '2-3' | '3-7' | '';
    shippingCOD: boolean;
    quantity: number;
    auctionDuration: number;
    endTime: string;
    shippingLocation: Prefecture | '';
    nekoposu: boolean;
    nekoCompact: boolean;
    nekoEazy: boolean;
    nekoEazySize: string;
    nekoEazyWeight: string;
    yuPacketMini: boolean;
    yuPacket: boolean;
    yuPacketPlus: boolean;
    yuPack: boolean;
    yuPackSize: string;
    yuPackWeight: string;
    customShippingMethods: any[];
    autoExtension: boolean;
    autoExtensionRate: number;
    autoExtensionCount: number;
    autoExtensionByBid: boolean;
    ratingRestriction: boolean;
    badRatingRestriction: boolean;
    bidderAuthentication: boolean;
    returnsAccepted: boolean;
    allowHtmlInDescription: boolean;
}

export interface ProductTemplate {
  id: string;
  name: string;
  folderId: string;
  order?: number;
  fields: ProductTemplateFieldType;
  createdAt: string;
  updatedAt: string;
  isDeleted?: boolean;
}

export function createTemplateFromProduct(product: Product, name: string, folderId: string = 'default'): ProductTemplate {
  return {
    id: crypto.randomUUID(),
    name,
    folderId,
    order: 0, // Default order, will be updated when added to store
    fields: {
      listingType: product.listingType,
      itemNumber: product.itemNumber,
      itemNumber2: product.itemNumber2,
      itemNumberPrefix: product.itemNumberPrefix,
      itemNumberSuffix: product.itemNumberSuffix,
      itemNumberSerial: product.itemNumberSerial,
      memoPrefix: product.memoPrefix,
      memoSuffix: product.memoSuffix,
      memoSerial: product.memoSerial,
      condition: product.condition,
      description: product.description,
      descriptionTemplateId: product.descriptionTemplateId,
      categoryYahoo: product.categoryYahoo || '', // Include Yahoo category
      shippingResponsibility: product.shippingResponsibility,
      shippingLater: product.shippingLater,
      shippingCOD: product.shippingCOD,
      quantity: product.quantity,
      auctionDuration: product.auctionDuration,
      endTime: product.endTime,
      shippingTime: product.shippingTime,
      shippingLocation: product.shippingLocation,
      nekoposu: product.nekoposu,
      nekoCompact: product.nekoCompact,
      nekoEazy: product.nekoEazy,
      nekoEazySize: product.nekoEazySize,
      nekoEazyWeight: product.nekoEazyWeight,
      yuPacketMini: product.yuPacketMini,
      yuPacket: product.yuPacket,
      yuPacketPlus: product.yuPacketPlus,
      yuPack: product.yuPack,
      yuPackSize: product.yuPackSize,
      yuPackWeight: product.yuPackWeight,
      customShippingMethods: [...product.customShippingMethods],
      autoExtension: product.autoExtension,
      autoExtensionRate: product.autoExtensionRate,
      autoExtensionCount: product.autoExtensionCount,
      autoExtensionByBid: product.autoExtensionByBid,
      ratingRestriction: product.ratingRestriction,
      badRatingRestriction: product.badRatingRestriction,
      bidderAuthentication: product.bidderAuthentication,
      returnsAccepted: product.returnsAccepted,
      allowHtmlInDescription: product.allowHtmlInDescription
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

export function applyTemplateToProduct(product: Product, template: ProductTemplate) {
  // Create a new product object with template fields but preserve the original price information
  const updatedProduct = {
    ...product,
    listingType: template.fields.listingType,
    itemNumber: template.fields.itemNumber,
    itemNumber2: template.fields.itemNumber2,
    itemNumberPrefix: template.fields.itemNumberPrefix,
    itemNumberSuffix: template.fields.itemNumberSuffix,
    itemNumberSerial: template.fields.itemNumberSerial,
    memoPrefix: template.fields.memoPrefix,
    memoSuffix: template.fields.memoSuffix,
    memoSerial: template.fields.memoSerial,
    condition: template.fields.condition,
    description: template.fields.description,
    descriptionTemplateId: template.fields.descriptionTemplateId,
    categoryYahoo: template.fields.categoryYahoo || product.categoryYahoo || '',
    shippingResponsibility: template.fields.shippingResponsibility,
    shippingLater: template.fields.shippingLater,
    shippingCOD: template.fields.shippingCOD,
    quantity: template.fields.quantity,
    auctionDuration: template.fields.auctionDuration,
    endTime: template.fields.endTime,
    shippingTime: template.fields.shippingTime,
    shippingLocation: template.fields.shippingLocation,
    nekoposu: template.fields.nekoposu,
    nekoCompact: template.fields.nekoCompact,
    nekoEazy: template.fields.nekoEazy,
    nekoEazySize: template.fields.nekoEazySize,
    nekoEazyWeight: template.fields.nekoEazyWeight,
    yuPacketMini: template.fields.yuPacketMini,
    yuPacket: template.fields.yuPacket,
    yuPacketPlus: template.fields.yuPacketPlus,
    yuPack: template.fields.yuPack,
    yuPackSize: template.fields.yuPackSize,
    yuPackWeight: template.fields.yuPackWeight,
    customShippingMethods: [...template.fields.customShippingMethods],
    autoExtension: template.fields.autoExtension,
    autoExtensionRate: template.fields.autoExtensionRate,
    autoExtensionCount: template.fields.autoExtensionCount,
    autoExtensionByBid: template.fields.autoExtensionByBid,
    ratingRestriction: template.fields.ratingRestriction,
    badRatingRestriction: template.fields.badRatingRestriction,
    bidderAuthentication: template.fields.bidderAuthentication,
    returnsAccepted: template.fields.returnsAccepted,
    allowHtmlInDescription: template.fields.allowHtmlInDescription
  };
  
  return updatedProduct;
}